<template>
  <div class="is-loading" v-if="isLoading">
    <b-spinner style="border-width: 2px" />
  </div>
  <div v-else>
    <div class="dynamic-form-datetime-picker" :class="{ 'd-flex': timeStyle === 'select_style' }">
      <DynamicFormDatePicker
        ref="datePicker"
        v-model="date"
        :hasError="hasError"
        :placeholder="placeholderForDate"
        @input="handleInput"
        :dateType="dateCondition"
        :dateOptions="dateTimeOptions"
        :disabled="disabled"
        :optionDisabler="(dateOption) => optionDisabler(dateOption, null)"
      />
      <DynamicFormTimePicker
        v-model="time"
        :hasError="hasError"
        :placeholder="placeholderForTime"
        @input="handleInput"
        :start-time="startTime"
        :end-time="endTime"
        :style-type="timeCondition"
        :slot-minutes="slotMinutes"
        :time-style="timeStyle"
        :custom-time-options="resolvedCustomTimeOptions"
        :disabled="disabled"
        :optionDisabler="(timeOption) => optionDisabler(date, timeOption)"
      />
    </div>
  </div>
</template>

<script>
import DynamicFormDatePicker from '@/components/DynamicForm/DatePicker.vue';
import DynamicFormTimePicker from '@/components/DynamicForm/TimePicker.vue';
import moment from 'moment';

export default {
  props: {
    value: [Date, String],
    placeholderForDate: {
      type: String,
      default: ' ',
    },
    timeStyle: {
      type: String,
      default: 'select_style',
    },
    placeholderForTime: {
      type: String,
      default: ' ',
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    dailyTime: {
      type: String,
      default: null,
    },
    timeCondition: {
      type: String,
      default: 'unlimited',
    },
    dateCondition: {
      type: String,
      default: 'unlimited',
    },
    dateTimeOptions: {
      type: Array,
      default: () => [],
    },
    startTime: {
      type: String,
      default: '00:00',
    },
    endTime: {
      type: String,
      default: '21:00',
    },
    slotMinutes: {
      type: [Number, String],
      default: 30,
    },
    customTimeOptions: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    optionDisabler: {
      type: Function,
      default: (date, time) => false, // eslint-disable-line
    },
  },
  components: {
    DynamicFormDatePicker,
    DynamicFormTimePicker,
  },
  data: () => ({
    date: null,
    time: null,
  }),
  computed: {
    resolvedCustomTimeOptions() {
      if (this.dailyTime === 'daily_flexible_time') {
        const selectedOption = (this.dateTimeOptions || []).find(option => option?.value === this.date)
        return Array.isArray(selectedOption?.time_options) ? selectedOption.time_options : []
      }

      // default and daily_fixed_time both use field-level options
      return Array.isArray(this.customTimeOptions) ? this.customTimeOptions : []
    },
  },
  watch: {
    value(val) {
      this.formatValueFromInput(val)
    },
    dateTimeOptions() {
      this.$nextTick(() => {
        const datePicker = this.$refs.datePicker;
        if (datePicker && typeof datePicker.triggerOptionsChangeDetection === 'function') {
          datePicker.triggerOptionsChangeDetection()
        }
      })
    },
  },
  mounted() {
    this.formatValueFromInput(this.value)
  },
  methods: {
    validate() {
      this.$refs.date.validate()
      this.$refs.time.validate()
    },
    formatValueFromInput(value) {
      if ([null, undefined].includes(value)) {
        return
      } else if (value instanceof Date) {
        this.date = value
        this.time = value
      } else if (typeof value == 'string') {
        const datetime = moment(value)
        this.date = datetime.format('YYYY-MM-DD')
        this.time = datetime.format('HH:mm')
      }
    },
    handleInput() {
      if ([this.date, this.time].includes(null)) {
        this.$emit('input', null)
        return
      }

      const datetime = moment(`${this.date} ${this.time}`)
      this.$emit('input', datetime.format('YYYY-MM-DD HH:mm:ss'))
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/base-colors.scss";

.dynamic-form-datetime-picker {
  margin: -8px;

  > * {
    flex: 1 1 50%;
    margin: 8px;
  }
}

.is-loading {
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: unset;
}
</style>
